import { useEffect, useState } from 'react'
import { loadGameweekStats, type GameweekStats } from '../storage'

export const EMPTY_GAMEWEEK_STATS: GameweekStats = {
  average: 0,
  highest: 0,
  highestUserId: null,
  highestDisplayName: null,
}

/**
 * The gameweek's average and highest score, for the two stats flanking the Predict page's GW
 * Points hero. Both used to be invented client-side from a pool of 500 imaginary players; they now
 * come from the same `season_history` rows every league table is computed from.
 *
 * Refetch rides `seasonVersion`, which is bumped whenever the client learns the server rescored
 * (`scoredDigest` moving, pushed over the realtime socket or read by the fallback poll). That is
 * the same signal that moves the user's own total and every league table, so the three can never
 * disagree by a poll interval, and there is no second timer.
 *
 * Zeros while a fetch is in flight or has failed, matching GW Points reading 0 before anything
 * lands — the tiles never go blank. The response is keyed by the gameweek it was asked for, so a
 * reply that arrives after the gameweek moved on is dropped rather than painted.
 *
 * @param enabled false until the two stats can actually be shown — before the deadline the hero
 * card is not rendered at all, and a request fired on mount would also be asking about the
 * starting-gameweek guess rather than the gameweek the server hands back.
 */
export function useGameweekStats(
  gameweek: number,
  seasonVersion: number,
  enabled: boolean
): GameweekStats {
  const [stats, setStats] = useState<{ gameweek: number; value: GameweekStats } | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    loadGameweekStats(gameweek)
      .then((value) => {
        if (!cancelled) setStats({ gameweek, value })
      })
      .catch(() => {
        // Zeros stay on screen. There is nothing to retry here: the next rescore refetches.
      })
    return () => {
      cancelled = true
    }
  }, [gameweek, seasonVersion, enabled])

  return stats && stats.gameweek === gameweek ? stats.value : EMPTY_GAMEWEEK_STATS
}
